// Pure mutators for the top-level item slice of a DraftScratchpad —
// creation (add) and clone (duplicate). Extracted from `store.ts` (#1441)
// so the store stays under the 600-line cap. Mirrors the
// `draft-box-actions.ts` contract: each function takes the current
// scratchpad and returns the next one, or `null` when the scratchpad is
// absent (the store callback then returns the existing state unchanged).
// The functions never mutate their input.
//
// Client ids are minted by the caller (the store) so zustand `set` is
// the only place that observes a new id — the helpers stay pure and
// id-allocation-free. `duplicateItem` takes a `mintPackageId` callback so
// each cloned package row gets a fresh client id without coupling this
// module to the id-sequence module.

import type { DraftScratchpad } from './types.js';

/**
 * Append a fresh empty item carrying `newItemId`. Returns `null` when the
 * scratchpad is absent.
 */
export function addItem(d: DraftScratchpad | null, newItemId: string): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    items: [
      ...d.items,
      {
        id: newItemId,
        name: '',
        brand: '',
        material: '',
        // WHY (#701): CONTAINER drafts allow null weight/volume,
        //   STANDARD drafts require non-null on save. Seed with
        //   null for CONTAINER so a blank field round-trips as
        //   null; STANDARD keeps 0 so the existing numeric-input
        //   UX (and the strict completeness gate) is unchanged.
        weight: d.kind === 'CONTAINER' ? null : 0,
        volume: d.kind === 'CONTAINER' ? null : 0,
        quantity: 1,
        // WHY (#1056): seed `null` so an unedited new item
        //   round-trips as "no product quantity entered yet"
        //   instead of duplicating the places count.
        productQuantity: null,
        description: '',
        photos: [],
        // WHY (#590): default mode is manual; user toggles
        //   dimensions when they want the package editor.
        dimensionsMode: false,
        packages: [],
      },
    ],
  };
}

/**
 * Clone the item identified by `itemId` (#1441). The copy carries
 * `newItemId`, sits immediately after its source, and every package row
 * gets a fresh client id with `id: null` so the next save creates new
 * server rows. No-op (returns `d` unchanged) when the item is not found;
 * returns `null` when the scratchpad is absent. Never mutates the input.
 *
 * INVARIANT (#1441 BR-5): `photos` is copied BY VALUE into a new array,
 *   so the clone shares each storage key string with its source. The
 *   backend photo cleanup is reference-counted, so a key referenced by
 *   two items stays valid until the last reference is gone — copying the
 *   key (not the object) is intentional and safe.
 */
export function duplicateItem(
  d: DraftScratchpad | null,
  itemId: string,
  newItemId: string,
  mintPackageId: () => string,
): DraftScratchpad | null {
  if (d === null) return null;
  const sourceIndex = d.items.findIndex((it) => it.id === itemId);
  if (sourceIndex === -1) return d;
  const source = d.items[sourceIndex]!;
  const copy = {
    id: newItemId,
    name: source.name,
    brand: source.brand,
    material: source.material,
    weight: source.weight,
    volume: source.volume,
    quantity: source.quantity,
    productQuantity: source.productQuantity,
    description: source.description,
    photos: [...source.photos],
    dimensionsMode: source.dimensionsMode,
    packages: source.packages.map((p) => ({ ...p, id: null, clientPackageId: mintPackageId() })),
  };
  return {
    ...d,
    items: [...d.items.slice(0, sourceIndex + 1), copy, ...d.items.slice(sourceIndex + 1)],
  };
}
